import { BehaviorSubject } from 'rxjs';

import { IIdentity } from '../models';

export class SelectionModel<T extends IIdentity> {
  readonly selectedValues$ = new BehaviorSubject<T[]>([]);

  private selection: { [key: string]: boolean } = {};
  private selectable: T[] = [];

  private isSelectAllActive = false;

  get selected(): T[] {
    return this.selectable.filter((x) => this.isSelected(x));
  }

  getIsSelectAllActive(): boolean {
    return this.isSelectAllActive;
  }

  sync(values: T[]): void {
    this.deselect(
      ...this.selected.filter((x) => !values.find((y) => y.id === x.id))
    );

    this.selectable = values;
  }

  toggle(value: T): void {
    this.isSelected(value) ? this.deselect(value) : this.select(value);
  }

  select(...values: T[]): void {
    if (!values.length) {
      return;
    }

    Object.assign(this.selection, ...values.map((x) => ({ [x.id]: true })));

    this.selectedValues$.next(this.selected);
    this.isSelectAllActive = this.isAllSelected();
  }

  deselect(...values: T[]): void {
    if (!values.length) {
      return;
    }

    values?.forEach((x) => {
      delete this.selection[x.id];
    });

    this.selectedValues$.next(this.selected);
    this.isSelectAllActive = this.isAllSelected();
  }

  selectAll(): void {
    this.select(...this.selectable);

    this.selectedValues$.next(this.selected);

    //This check is needed in case there are no selectable items and the user checks unchecks the
    // select all checkbox, so that it's set correctly according to the user interaction
    if (this.selectable.length === 0 && this.isSelectAllActive) {
      this.isSelectAllActive = false;
    } else {
      this.isSelectAllActive = true;
    }
  }

  clear(): void {
    this.selection = {};
    this.selectedValues$.next([]);

    this.isSelectAllActive = false;
  }

  isSelected(value: T): boolean {
    if (!this.selection) {
      return false;
    }

    return value.id in this.selection;
  }

  hasValue(): boolean {
    return !!(Object.keys(this.selection) ?? []).length;
  }

  isAllSelected(): boolean {
    return this.selectable.length > 0
      ? this.selectable.every((x) => this.isSelected(x))
      : false;
  }
}
